export interface ClerkPortalConfig {
  portalUrl?: string;
  returnParam?: string;
}

export const DEFAULT_CLERK_PORTAL_URL = "https://home.altadairyinsight.com/anmelden";

export function isClerkPublishableKeyForEnvironment(
  key: string | undefined,
  development: boolean,
): boolean {
  return Boolean(key?.trim().startsWith(development ? "pk_test_" : "pk_live_"));
}

/**
 * Build a return link only when the portal's agreed query parameter is
 * configured. The return target is always this app's own /login route.
 */
export function buildClerkPortalLoginUrl(
  config: ClerkPortalConfig,
  currentOrigin: string,
): string | null {
  const returnParam = config.returnParam?.trim();
  if (!returnParam) return null;

  let portal: URL;
  try {
    portal = new URL(config.portalUrl?.trim() || DEFAULT_CLERK_PORTAL_URL);
  } catch {
    return null;
  }
  if (portal.protocol !== "https:" || !/^[A-Za-z][A-Za-z0-9_.-]*$/.test(returnParam)) {
    return null;
  }

  // Derive the origin from the running app rather than accepting a caller-
  // supplied redirect URL. This intentionally discards arbitrary query/hash
  // values and never forwards a user-controlled path.
  let origin: string;
  try {
    const appUrl = new URL(currentOrigin);
    if (appUrl.protocol !== "https:" && appUrl.hostname !== "localhost" && appUrl.hostname !== "127.0.0.1") {
      return null;
    }
    origin = appUrl.origin;
  } catch {
    return null;
  }
  portal.searchParams.set(returnParam, new URL("/login", origin).toString());
  return portal.toString();
}

export function clerkPortalLoginUrl(): string | null {
  if (
    !isClerkPublishableKeyForEnvironment(
      import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
      import.meta.env.DEV,
    )
    || isClerkSatelliteConfigurationMissing()
    || isDevClerkConfigurationMissing()
  ) {
    return null;
  }
  return buildClerkPortalLoginUrl(
    {
      portalUrl: import.meta.env.VITE_CLERK_PORTAL_URL,
      returnParam: import.meta.env.VITE_CLERK_PORTAL_RETURN_PARAM,
    },
    window.location.origin,
  );
}

export function clerkPortalUnavailableReason(): string {
  if (!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY?.trim()) {
    return "Die Clerk-Anmeldung ist noch nicht eingerichtet. Bitte den Administrator kontaktieren.";
  }
  if (!isClerkPublishableKeyForEnvironment(
    import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
    import.meta.env.DEV,
  )) {
    return import.meta.env.DEV
      ? "Für die Development-Vorschau wird ein Clerk-Testschlüssel (pk_test) benötigt. Live-Schlüssel gehören ausschließlich in die Deployment-Secrets."
      : "Für diese veröffentlichte App wird ein Clerk-Live-Schlüssel (pk_live) in den Deployment-Secrets benötigt.";
  }
  if (import.meta.env.DEV && !isClerkDevelopmentPortalConfigured()) {
    return "Für Development muss VITE_CLERK_PORTAL_URL auf ein Clerk-Portal mit pk_test zeigen; das Live-Portal kann keine Development-Sitzungen ausstellen.";
  }
  if (isClerkSatelliteConfigurationMissing()) {
    return "Die aktivierte Clerk-Satellitenanmeldung benötigt eine konfigurierte VITE_CLERK_SATELLITE_DOMAIN für den Hostnamen dieser App.";
  }
  if (isDevClerkConfigurationMissing()) {
    if (!import.meta.env.VITE_CLERK_SATELLITE_DOMAIN?.trim()) {
      return "Die Development-Satellitenanmeldung benötigt eine konfigurierte Clerk-Satellitendomäne.";
    }
  }
  return "Die Alta-Anmeldung ist noch nicht konfiguriert. Bitte die Clerk-Portal-URL und den vereinbarten Rückgabeparameter durch den Administrator konfigurieren lassen.";
}

function isDevClerkConfigurationMissing(): boolean {
  if (!import.meta.env.DEV) return false;
  if (!isClerkDevelopmentPortalConfigured()) return true;
  return import.meta.env.VITE_CLERK_IS_SATELLITE === "true"
    && !import.meta.env.VITE_CLERK_SATELLITE_DOMAIN?.trim();
}

export function isClerkSatelliteConfigurationMissing(): boolean {
  if (import.meta.env.VITE_CLERK_IS_SATELLITE !== "true") return false;
  return !import.meta.env.VITE_CLERK_SATELLITE_DOMAIN?.trim()
    || (import.meta.env.DEV && !isClerkDevelopmentPortalConfigured());
}

export function isClerkDevelopmentPortalConfigured(): boolean {
  const configuredPortal = import.meta.env.VITE_CLERK_PORTAL_URL?.trim();
  if (!configuredPortal) return false;
  try {
    const portal = new URL(configuredPortal);
    const livePortal = new URL(DEFAULT_CLERK_PORTAL_URL);
    return portal.protocol === "https:" && portal.origin !== livePortal.origin;
  } catch {
    return false;
  }
}

export function clearClerkAuthSuppressions(): void {
  sessionStorage.removeItem("alta_clerk_exchange_failed");
  sessionStorage.removeItem("alta_logout_requested");
}

export function hasClerkExchangeFailed(): boolean {
  return sessionStorage.getItem("alta_clerk_exchange_failed") === "1";
}

export function hasExplicitLogoutRequest(): boolean {
  return sessionStorage.getItem("alta_logout_requested") === "1";
}

export function markClerkExchangeFailed(): void {
  sessionStorage.setItem("alta_clerk_exchange_failed", "1");
}

export function markExplicitLogoutRequest(): void {
  sessionStorage.setItem("alta_logout_requested", "1");
}